import axios from "axios";

import {
    PriceChangeSchedulePlatform,
    PriceChangeScheduleStatus,
    PriceChangeScheduleSyncStatus,
} from "../../../utils/enums";

const headers = (token: string) => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "Admin-Request": "true",
});

export interface IPriceChangeSchedulePlatformEntry {
    id: string;
    scheduleId: string;
    platform: PriceChangeSchedulePlatform;
    syncStatus: PriceChangeScheduleSyncStatus;
    retryCount: number;
    syncedAt: string | null;
    errorMessage: string | null;
}

export interface IPriceChangeSchedule {
    id: string;
    variantId: string;
    sellingPrice: number;
    baseSellingPrice: number;
    startsAt: string;
    endsAt: string;
    status: PriceChangeScheduleStatus;
    executedAt: string | null;
    createdBy: string | null;
    notes: string | null;
    meta: Record<string, any> | null;
    platforms: IPriceChangeSchedulePlatformEntry[];
    createdAt: string;
    updatedAt: string;
}

export interface IPriceChangeScheduleListPage {
    records: (IPriceChangeSchedule & {
        variant?: { id: string; title: string; sku: string; price: number };
    })[];
    cursors: { next?: string; previous?: string };
}

export interface IBulkCreateResult {
    created: { row: number; variantId: string; scheduleId: string }[];
    errors: { row: number; variantId: string; reason: string }[];
}

export const getAllSchedules = async (
    token: string,
    params?: {
        limit?: number;
        cursor?: string;
        direction?: string;
        status?: PriceChangeScheduleStatus;
    }
): Promise<IPriceChangeScheduleListPage> => {
    const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/price-schedules`, {
        headers: headers(token),
        params,
    });
    return data;
};

export const getSchedulesForVariant = async (
    token: string,
    variantId: string
): Promise<IPriceChangeSchedule[]> => {
    const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/variants/${variantId}/price-schedules`,
        { headers: headers(token) }
    );
    return data;
};

export const getSchedule = async (
    token: string,
    scheduleId: string
): Promise<IPriceChangeSchedule & { history?: any[] }> => {
    const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/price-schedules/${scheduleId}`,
        { headers: headers(token) }
    );
    return data;
};

export const createSchedule = async (
    token: string,
    variantId: string,
    body: {
        sellingPrice: number;
        startsAt: string;
        endsAt: string;
        platforms: PriceChangeSchedulePlatform[];
        notes?: string;
    }
): Promise<IPriceChangeSchedule> => {
    const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/variants/${variantId}/price-schedules`,
        body,
        { headers: headers(token) }
    );
    return data;
};

export const bulkCreateSchedules = async (
    token: string,
    body: {
        startsAt: string;
        endsAt: string;
        platforms: PriceChangeSchedulePlatform[];
        notes?: string;
        rows: { variantId: string; sellingPrice: number }[];
    }
): Promise<IBulkCreateResult> => {
    const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/price-schedules/bulk`,
        body,
        { headers: headers(token) }
    );
    return data;
};

export const updateSchedule = async (
    token: string,
    scheduleId: string,
    body: any
): Promise<IPriceChangeSchedule> => {
    const { data } = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/price-schedules/${scheduleId}`,
        body,
        { headers: headers(token) }
    );
    return data;
};

export const cancelSchedule = async (
    token: string,
    scheduleId: string
): Promise<void> => {
    await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/price-schedules/${scheduleId}`, {
        headers: headers(token),
    });
};

export const exportSchedulesCsv = async (
    token: string,
    params?: { status?: PriceChangeScheduleStatus; fromDate?: string; toDate?: string }
): Promise<string> => {
    const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/price-schedules/export`, {
        headers: headers(token),
        params,
        responseType: "text",
    });
    return data;
};

export const getNotificationConfig = async (
    token: string
): Promise<{ emails: string[] }> => {
    const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/price-schedules/notification-config`,
        { headers: headers(token) }
    );
    return data;
};

export const updateNotificationConfig = async (
    token: string,
    emails: string[]
): Promise<{ emails: string[] }> => {
    const { data } = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/price-schedules/notification-config`,
        { emails },
        { headers: headers(token) }
    );
    return data;
};
